import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { getSchematisationsApi } from "../apiConfiguration";

import { ID } from "./queryClient";

const schematisationsApi = getSchematisationsApi();

export function useSchematisation(schematisationId: ID) {
  return useQuery({
    queryKey: ["schematisation", schematisationId],
    queryFn: () =>
      schematisationsApi.schematisationsRead({
        id: schematisationId ?? 0,
      }),
    enabled: !!schematisationId,
  });
}

export function useLatestRevision(schematisationId: ID) {
  return useQuery({
    queryKey: ["last_revision", schematisationId],
    queryFn: () =>
      schematisationsApi.schematisationsLatestRevision({
        id: schematisationId ?? 0,
      }),
    enabled: !!schematisationId,
  });
}

export function useRevisions(schematisationId: ID) {
  return useQuery({
    queryKey: ["schematisation-revisions", schematisationId],
    queryFn: () => {
      if (!schematisationId) throw new Error("schematisationId is required");
      return schematisationsApi.schematisationsRevisionsList({
        schematisationPk: schematisationId,
        committed: true,
        limit: 1000,
      });
    },
    enabled: !!schematisationId,
  });
}

export function useRevision(schematisationId: ID, revisionId: ID) {
  return useQuery({
    queryKey: ["revision", schematisationId, revisionId],
    queryFn: () => {
      if (!schematisationId || !revisionId)
        throw new Error("schematisationId and revisionId are required");
      return schematisationsApi.schematisationsRevisionsRead({
        id: revisionId,
        schematisationPk: schematisationId,
      });
    },
    enabled: !!(schematisationId && revisionId),
  });
}

export function useSchematisationModels(schematisationId: ID, revisionId: ID) {
  // Note that this includes models with disabled = true! Filter them out manually if needed.
  return useQuery({
    queryKey: ["schematisationmodels", schematisationId, revisionId],
    queryFn: () => {
      if (!schematisationId || !revisionId)
        throw new Error("schematisationId and revisionId are required");
      return schematisationsApi.schematisationsRevisionsThreedimodels({
        id: revisionId,
        schematisationPk: schematisationId,
      });
    },
    enabled: !!(schematisationId && revisionId),
  });
}

export function useSchematisationRevisionRasters(schematisationId: ID, revisionId: ID) {
  return useQuery({
    queryKey: ["schematisationrevisionrasters", schematisationId, revisionId],
    queryFn: () => {
      if (!schematisationId || !revisionId)
        throw new Error("schematisationId and revisionId are required");
      return schematisationsApi.schematisationsRevisionsRastersList({
        revisionPk: revisionId,
        schematisationPk: schematisationId,
      });
    },
    enabled: !!(schematisationId && revisionId),
  });
}

export function useInvalidateSchematisationModel(schematisationId: ID, revisionId: ID) {
  // Return a function that can invalidate the cache of useSchematisationModels
  const queryClient = useQueryClient();

  const invalidateSchematisationModel = useCallback(() => {
    if (schematisationId && revisionId) {
      queryClient.invalidateQueries({
        queryKey: ["schematisationmodels", schematisationId, revisionId],
      });
    }
  }, [queryClient, schematisationId, revisionId]);

  return invalidateSchematisationModel;
}

export type DeleteSchematisationRevisionItem = {
  schematisationId: ID;
  revisionId: ID;
  revisionNumber?: number;
};

export function useDeleteSchematisationRevision(
  schematisationId: ID,
  revisionId: ID,
  revisionNumber?: number
) {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    if (schematisationId && revisionId && revisionNumber !== undefined) {
      await schematisationsApi.schematisationsRevisionsDelete({
        id: revisionId,
        schematisationPk: schematisationId,
        data: {
          number: revisionNumber,
        },
      });

      queryClient.invalidateQueries({ queryKey: ["schematisation-revisions", schematisationId] });
      queryClient.invalidateQueries({ queryKey: ["revision", schematisationId, revisionId] });
    }
  }, [queryClient, schematisationId, revisionId, revisionNumber]);
}

export function useDeleteMultipleSchematisationRevisions(
  schemas: DeleteSchematisationRevisionItem[]
) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all(
      schemas.map(async (schema) => {
        if (schema.schematisationId && schema.revisionId && schema.revisionNumber !== undefined)
          return schematisationsApi.schematisationsRevisionsDelete({
            id: schema.revisionId,
            schematisationPk: schema.schematisationId,
            data: {
              number: schema.revisionNumber,
            },
          });
      })
    );
    queryClient.invalidateQueries({ queryKey: ["schematisation-revisions"] });
    queryClient.invalidateQueries({ queryKey: ["revisions"] });
  }, [queryClient, schemas]);
}

export function useDeleteSchematisation(schematisationId: ID) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    if (schematisationId) {
      await schematisationsApi.schematisationsDelete({
        id: schematisationId,
      });

      queryClient.invalidateQueries({ queryKey: ["schematisation", schematisationId] });
    }
  }, [queryClient, schematisationId]);
}

export function useDeleteMultipleSchematisation(schematisationIds: ID[]) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all(
      schematisationIds.map(async (schematisationId) => {
        if (schematisationId)
          return schematisationsApi.schematisationsDelete({
            id: schematisationId,
          });
      })
    );
    queryClient.invalidateQueries({ queryKey: ["schematisations"] });
  }, [queryClient, schematisationIds]);
}

export function useRevisionTasks(schematisationId: ID, revisionId: ID) {
  return useQuery({
    queryKey: ["revision-tasks", schematisationId, revisionId],
    queryFn: () => {
      if (!schematisationId || !revisionId)
        throw new Error("schematisationId and revisionId are required");
      return schematisationsApi.schematisationsRevisionsTasksList({
        revisionPk: revisionId,
        schematisationPk: schematisationId,
      });
    },
  });
}

export function useLatestRevisionTask(schematisationId: ID, revisionId: ID) {
  const tasksResponse = useRevisionTasks(schematisationId, revisionId);

  if (tasksResponse.isSuccess && tasksResponse.data.results[0]) {
    return tasksResponse.data.results[0];
  } else {
    return null;
  }
}

export function useThreedimodelsRasters(id: string) {
  return useQuery({
    queryKey: ["threedimodelsrasters", id],
    queryFn: () =>
      schematisationsApi.threedimodelsRastersList({
        threedimodelPk: Number(id),
      }),
    enabled: !!id && id !== "",
  });
}

export function useSavedStates(id: string) {
  return useQuery({
    queryKey: ["savedstates", id],
    queryFn: () =>
      schematisationsApi.threedimodelsSavedStatesList({
        threedimodelPk: Number(id),
      }),
    enabled: !!id && id !== "",
  });
}
